import { cva, type VariantProps } from 'class-variance-authority'
import { CreditCardIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import type { CardBrand } from './billing.interface'

const billingCardBrandIconVariants = cva('inline-flex items-center justify-center', {
  variants: {
    size: {
      sm: 'size-6 text-sm',
      md: 'size-8 text-base',
      lg: 'size-10 text-lg',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

export interface BillingCardBrandIconProps
  extends VariantProps<typeof billingCardBrandIconVariants> {
  brand: CardBrand
  className?: string
}

const brandColorMap: Record<CardBrand, string> = {
  visa: 'text-blue-600 dark:text-blue-400',
  mastercard: 'text-orange-600 dark:text-orange-400',
  amex: 'text-blue-700 dark:text-blue-300',
  discover: 'text-orange-500 dark:text-orange-300',
}

const brandLabelMap: Record<CardBrand, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
}

const BillingCardBrandIconBase: React.FC<BillingCardBrandIconProps> = ({
  brand,
  size,
  className,
}): ReactNode => (
  <span
    className={cn(
      'billing-card-brand-icon',
      billingCardBrandIconVariants({ size }),
      brandColorMap[brand],
      className
    )}
    role="img"
    aria-label={brandLabelMap[brand]}
  >
    <CreditCardIcon className="size-full" />
  </span>
)

export const BillingCardBrandIcon = memo(BillingCardBrandIconBase)

BillingCardBrandIcon.displayName = 'BillingCardBrandIcon'

export { billingCardBrandIconVariants }
